import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import TaxWeekMonthSelect from '@nxsys/components/AppTaxWeekMonthSelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { P30P32RecordType } from '@nxsys/types/models/hmrcReports';
import { Form, FormProps, Typography } from 'antd';
import { toNumber } from 'lodash';
import React, { useEffect, useState } from 'react';
import AppRateInput from '@nxsys/components/AppRateInput';

export type P30FormValues = {
  tax: number;
  receivedFromHMRCToRefundTax: number;
  totalCisDeduction: number;
  cisDeductionsSuffered: number;
  grossStudentLoanDeductions: number;
  grossPostgraduateLoanDeductions: number;
  grossEmployeeNI: number;
  grossEmployersNI: number;
  totalStatutoryPayRecovered: number;
  totalNICCompensation: number;
  receivedFromHMRCToPayStatutoryPay: number;
  empAllowanceClaimed: number;
  appLevyPayable: number;
  previousTaxMonthsPaidYtd: number;
  previousTaxMonthsDebtYtd: number;
  previousTaxMonthsShortFall: number;
  adjustment: number;
  currentTaxMonthPaid: number;
  paymentDate: string;
  netIncomeTax?: number;
  netNIContributions?: number;
  netAdjustment?: number;
  dueInCurrentPeriod?: number;
};

type Props = {
  defaultValues?: Partial<P30FormValues>;
  open: boolean;
  onClose: () => void;
  data: P30P32RecordType;
  onChangeTaxMonth: (value: number) => void;
  onSave: (values: P30FormValues) => Promise<void>;
  onExport: (data: P30P32RecordType[]) => void;
  onDownload?: (data: P30P32RecordType) => void;
  isDownloading?: boolean;
};

const innerDefaultValues = {
  tax: 0,
  receivedFromHMRCToRefundTax: 0,
  totalCisDeduction: 0,
  cisDeductionsSuffered: 0,
  grossStudentLoanDeductions: 0,
  grossPostgraduateLoanDeductions: 0,
  grossEmployeeNI: 0,
  grossEmployersNI: 0,
  totalStatutoryPayRecovered: 0,
  totalNICCompensation: 0,
  receivedFromHMRCToPayStatutoryPay: 0,
  empAllowanceClaimed: 0,
  appLevyPayable: 0,
  previousTaxMonthsPaidYtd: 0,
  previousTaxMonthsDebtYtd: 0,
  previousTaxMonthsShortFall: 0,
  adjustment: 0,
  currentTaxMonthPaid: 0,
  paymentDate: 0,
};

const ViewP30Modal: React.FC<Props> = ({
  defaultValues = innerDefaultValues,
  open,
  onClose,
  data,
  onChangeTaxMonth,
  onSave,
  onExport,
  onDownload,
  isDownloading,
}) => {
  const { messages } = useTypedIntl();
  const [form] = Form.useForm<P30FormValues>();
  const values = Form.useWatch([], form) ?? {};
  const [isSaving, setIsSaving] = useState(false);

  const onFinish: FormProps<P30FormValues>['onFinish'] = (values) => {
    const mergedValues: P30FormValues = {
      ...defaultValues,
      ...values,
    };
    setIsSaving(true);
    onSave?.(mergedValues).finally(() => {
      setIsSaving(false);
    });
  };

  const onFinishFailed: FormProps<P30FormValues>['onFinishFailed'] = (
    errorInfo,
  ) => {
    console.log('Failed:', errorInfo);
  };

  useEffect(() => {
    // Only seed the form with the known scalar fields. The raw `data` record
    // carries the full `payrollsInThisMonth` payload, which AntD Form would
    // store and `Form.useWatch([])` would then re-traverse on every computed
    // field update — freezing the main thread on open.
    form.setFieldsValue(defaultValues as P30FormValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    form.setFieldValue(
      'netIncomeTax',
      Number(
        (
          (values?.tax || 0) +
          (toNumber(values?.receivedFromHMRCToRefundTax) || 0) -
          (toNumber(values?.cisDeductionsSuffered) || 0) +
          (values?.totalCisDeduction || 0) +
          (values?.grossStudentLoanDeductions || 0) +
          (values?.grossPostgraduateLoanDeductions || 0)
        ).toFixed(2),
      ),
    );
  }, [
    values?.tax,
    values?.receivedFromHMRCToRefundTax,
    values?.cisDeductionsSuffered,
    values?.totalCisDeduction,
    values?.grossStudentLoanDeductions,
    values?.grossPostgraduateLoanDeductions,
  ]);

  useEffect(() => {
    form.setFieldValue(
      'netNIContributions',
      Number(
        (
          (values?.grossEmployeeNI || 0) +
          (values?.grossEmployersNI || 0) -
          (values?.totalStatutoryPayRecovered || 0) -
          (values?.totalNICCompensation || 0) +
          (toNumber(values?.receivedFromHMRCToPayStatutoryPay) || 0) -
          (values?.empAllowanceClaimed || 0) +
          (values?.appLevyPayable || 0)
        ).toFixed(2),
      ),
    );
  }, [
    values?.grossEmployeeNI,
    values?.grossEmployersNI,
    values?.totalStatutoryPayRecovered,
    values?.totalNICCompensation,
    values?.receivedFromHMRCToPayStatutoryPay,
    values?.empAllowanceClaimed,
    values?.appLevyPayable,
  ]);

  useEffect(() => {
    form.setFieldValue(
      'netAdjustment',
      Number(
        (
          (values?.previousTaxMonthsShortFall || 0) +
          (toNumber(values?.adjustment) || 0)
        ).toFixed(2),
      ),
    );
  }, [values?.previousTaxMonthsShortFall, values?.adjustment]);

  useEffect(() => {
    form.setFieldValue(
      'dueInCurrentPeriod',
      Number(
        (
          (values?.tax || 0) +
          (toNumber(values?.receivedFromHMRCToRefundTax) || 0) -
          (toNumber(values?.cisDeductionsSuffered) || 0) +
          (values?.totalCisDeduction || 0) +
          (values?.grossStudentLoanDeductions || 0) +
          (values?.grossPostgraduateLoanDeductions || 0) +
          (values?.grossEmployeeNI || 0) +
          (values?.grossEmployersNI || 0) -
          (values?.totalStatutoryPayRecovered || 0) -
          (values?.totalNICCompensation || 0) +
          (toNumber(values?.receivedFromHMRCToPayStatutoryPay) || 0) -
          (values?.empAllowanceClaimed || 0) +
          (values?.appLevyPayable || 0) +
          (values?.previousTaxMonthsShortFall || 0) +
          (toNumber(values?.adjustment) || 0)
        ).toFixed(2),
      ),
    );
  }, [
    values?.tax,
    values?.receivedFromHMRCToRefundTax,
    values?.cisDeductionsSuffered,
    values?.totalCisDeduction,
    values?.grossStudentLoanDeductions,
    values?.grossPostgraduateLoanDeductions,
    values?.grossEmployeeNI,
    values?.grossEmployersNI,
    values?.totalStatutoryPayRecovered,
    values?.totalNICCompensation,
    values?.receivedFromHMRCToPayStatutoryPay,
    values?.empAllowanceClaimed,
    values?.appLevyPayable,
    values?.previousTaxMonthsShortFall,
    values?.adjustment,
  ]);

  return (
    <AppModalContainer
      isOpen={open}
      toggle={onClose}
      title={messages['report.hmrc.viewDetails']}
      width="80vw"
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: onClose,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => form.submit(),
          loading: isSaving,
          disabled: isSaving,
        },
      ]}
    >
      <div className="flex items-end justify-between w-full pb-4">
        {/* LEFT SIDE */}
        <div className="flex items-center gap-3">
          <div>
            <Typography.Text className="text-gray-600">
              Payment Company:
            </Typography.Text>
            <AppInput
              className="mt-2"
              value={data?.companyRef?.companyName}
              disabled
            />
          </div>

          <div>
            <Typography.Text className="text-gray-600">
              Tax Month:
            </Typography.Text>
            <TaxWeekMonthSelect
              placeholder="Select Tax Month"
              className="mt-2"
              paymentFrequency="MONTHLY"
              initValue={data?.taxMonth}
              allowClear={false}
              onChange={(value) => {
                onChangeTaxMonth(value!);
              }}
            />
          </div>
        </div>

        {/* RIGHT SIDE */}
        <AppButton
          type="primary"
          name={messages['report.hmrc.p30.downloadP30']}
          loading={isDownloading}
          onClick={() => {
            if (onDownload) {
              onDownload(data);
            } else {
              onExport([data]);
            }
          }}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* === Column 1 - TAX DETAILS === */}
        <AppCard title="Tax Details">
          <AppFormContainer
            form={form}
            initialValues={defaultValues}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            labelCol={{ span: 12 }}
            wrapperCol={{ span: 12 }}
            className="p-[20px]"
          >
            <Form.Item label="Gross tax" name="tax">
              <AppInput placeholder="0" disabled />
            </Form.Item>

            <Form.Item
              label="Received from HMRC to"
              name="receivedFromHMRCToRefundTax"
            >
              <AppRateInput placeholder="0" />
            </Form.Item>

            <Form.Item label="Gross CIS deductions" name="totalCisDeduction">
              <AppInput placeholder="0" disabled />
            </Form.Item>

            <Form.Item
              label="CIS deductions suffered"
              name="cisDeductionsSuffered"
            >
              <AppRateInput placeholder="0" />
            </Form.Item>

            <Form.Item
              label="Gross Student Loan deductions"
              name="grossStudentLoanDeductions"
            >
              <AppInput placeholder="0" disabled />
            </Form.Item>

            <Form.Item
              label="Gross Postgraduate Loan deductions"
              name="grossPostgraduateLoanDeductions"
            >
              <AppInput placeholder="0" disabled />
            </Form.Item>

            <Form.Item label="Net Income Tax" name="netIncomeTax">
              <AppInput placeholder="0" disabled />
            </Form.Item>
          </AppFormContainer>
        </AppCard>

        {/* === Column 2 - NATIONAL INSURANCE DETAILS === */}
        <AppCard title="National Insurance Details">
          <AppFormContainer
            form={form}
            initialValues={defaultValues}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            labelCol={{ span: 12 }}
            wrapperCol={{ span: 12 }}
            className="p-[20px]"
          >
            {/* Employee NICs */}
            <Form.Item label="Employee NICs" name="grossEmployeeNI">
              <AppInput placeholder="0" disabled />
            </Form.Item>

            {/* Employer NICs */}
            <Form.Item label="Employer NICs" name="grossEmployersNI">
              <AppInput placeholder="0" disabled />
            </Form.Item>

            {/* Total statutory pay recovered */}
            <Form.Item
              label="Total statutory pay recovered"
              name="totalStatutoryPayRecovered"
            >
              <AppInput placeholder="0" disabled />
            </Form.Item>

            {/* Total statutory pay NIC */}
            <Form.Item
              label="Total statutory pay NIC compensation"
              name="totalNICCompensation"
            >
              <AppInput placeholder="0" disabled />
            </Form.Item>

            {/* Statutory pay recovered breakdown (display only, from record) */}
            <Form.Item label="SMP recovered">
              <AppInput
                value={Number(data?.smpRecovered ?? 0).toFixed(2)}
                disabled
              />
            </Form.Item>
            <Form.Item label="NIC compensation on SMP">
              <AppInput
                value={Number(data?.NICCompensationOnSMP ?? 0).toFixed(2)}
                disabled
              />
            </Form.Item>
            <Form.Item label="SPP recovered">
              <AppInput
                value={Number(data?.sppRecovered ?? 0).toFixed(2)}
                disabled
              />
            </Form.Item>
            <Form.Item label="NIC compensation on SPP">
              <AppInput
                value={Number(data?.NICCompensationOnSPP ?? 0).toFixed(2)}
                disabled
              />
            </Form.Item>
            <Form.Item label="SPP (additional) recovered">
              <AppInput
                value={Number(data?.sppaRecovered ?? 0).toFixed(2)}
                disabled
              />
            </Form.Item>
            <Form.Item label="NIC compensation on SPP (additional)">
              <AppInput
                value={Number(data?.NICCompensationOnSPPA ?? 0).toFixed(2)}
                disabled
              />
            </Form.Item>
            <Form.Item label="SAP recovered">
              <AppInput
                value={Number(data?.sapRecovered ?? 0).toFixed(2)}
                disabled
              />
            </Form.Item>
            <Form.Item label="NIC compensation on SAP">
              <AppInput
                value={Number(data?.NICCompensationOnSAP ?? 0).toFixed(2)}
                disabled
              />
            </Form.Item>
            <Form.Item label="SNCP recovered">
              <AppInput
                value={Number(data?.sncpRecovered ?? 0).toFixed(2)}
                disabled
              />
            </Form.Item>
            <Form.Item label="NIC compensation on SNCP">
              <AppInput
                value={Number(data?.NICCompensationOnSNCP ?? 0).toFixed(2)}
                disabled
              />
            </Form.Item>

            {/* Received from HMRC to pay statutory pay */}
            <Form.Item
              label="Received from HMRC to pay statutory pay"
              name="receivedFromHMRCToPayStatutoryPay"
            >
              <AppRateInput placeholder="0" />
            </Form.Item>

            {/* Employment Allowance claim */}
            <Form.Item
              label="Employment Allowance claim"
              name="empAllowanceClaimed"
            >
              <AppInput placeholder="0" disabled />
            </Form.Item>

            {/* Apprenticeship Levy */}
            <Form.Item label="Apprenticeship Levy" name="appLevyPayable">
              <AppInput placeholder="0" disabled />
            </Form.Item>

            {/* Net NICs */}
            <Form.Item label="Net NICs" name="netNIContributions">
              <AppInput placeholder="0.00" disabled />
            </Form.Item>
          </AppFormContainer>
        </AppCard>

        {/* === Column 3 - YEAR TO DATE === */}
        <AppCard title="Year to Date">
          <AppFormContainer
            form={form}
            initialValues={defaultValues}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            labelCol={{ span: 12 }}
            wrapperCol={{ span: 12 }}
            className="p-[20px]"
          >
            <Form.Item
              label="Due in previous months"
              name="previousTaxMonthsDebtYtd"
            >
              <AppInput placeholder="0" disabled />
            </Form.Item>

            <Form.Item
              label="Paid in previous months"
              name="previousTaxMonthsPaidYtd"
            >
              <AppInput placeholder="0" disabled />
            </Form.Item>

            <Form.Item label="Shortfall" name="previousTaxMonthsShortFall">
              <AppInput placeholder="0" disabled />
            </Form.Item>

            <Form.Item label="Adjustment" name="adjustment">
              <AppRateInput placeholder="0" />
            </Form.Item>

            <Form.Item label="Net Adjustment" name="netAdjustment">
              <AppInput placeholder="0" disabled />
            </Form.Item>
          </AppFormContainer>
        </AppCard>
      </div>
      <AppCard className="mt-4">
        <AppFormContainer
          form={form}
          initialValues={defaultValues}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          labelCol={{ span: 12 }}
          wrapperCol={{ span: 12 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Form.Item
              label="Amount due in current period"
              name="dueInCurrentPeriod"
              className="mb-0 p-[20px]"
            >
              <AppInput placeholder="0" disabled />
            </Form.Item>

            <Form.Item
              label="Amount paid in current period"
              name="currentTaxMonthPaid"
              className="mb-0 p-[20px]"
            >
              <AppRateInput placeholder="0" />
            </Form.Item>

            <Form.Item
              label="Payment Date"
              name="paymentDate"
              className="mb-0 p-[20px]"
            >
              <AppDatePicker placeholder="Payment Date" format="DD/MM/YYYY" />
            </Form.Item>
          </div>
        </AppFormContainer>
      </AppCard>
    </AppModalContainer>
  );
};

export default ViewP30Modal;
